/**
 * Full-page layout for the self-service "Create workspace" wizard.
 *
 * Structure (matches the onboarding design):
 *   - Top bar: logo | "Create workspace", with Back / Leave on the right
 *   - Left: dark step sidebar (hidden below lg)
 *   - Content: centered column over the faint ring-buoy patent artwork,
 *     with Help Center / Contact support pinned bottom-right
 *
 * This is a routed page layout, deliberately separate from the modal-based
 * `lib/base/wizard` (which renders inside FullscreenModal/SlideoutMenu and
 * owns its own step indicator and footer CTAs).
 */
import './self-service-layout.css';

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useTitle } from '@/hooks/useTitle';
import { StepSidebar } from '@/lib/self-service/components/StepSidebar';

interface SelfServiceLayoutProps {
  /** Zero-based index of the active step (drives the sidebar states). */
  currentStepIndex: number;
  /** Destination for the top-bar Back link. */
  backTo: string;
  /** Page title and top-bar flow label. Defaults to "Create workspace". */
  title?: string;
  /** Destination for the top-bar Leave link. Defaults to "/". */
  leaveTo?: string;
  /** Steps rendered in the sidebar. Defaults to the self-service wizard's step registry. */
  steps?: readonly { id: string; label: string }[];
  /** Resolves a step's href in the sidebar. Defaults to the self-service step path helper. */
  stepPath?: (stepId: string) => string;
  children: ReactNode;
}

const LEAVE_PATH = '/';

export function SelfServiceLayout({
  currentStepIndex,
  backTo,
  title = 'Create workspace',
  leaveTo = LEAVE_PATH,
  steps,
  stepPath,
  children,
}: SelfServiceLayoutProps) {
  useTitle(title);

  return (
    <div className='flex min-h-dvh flex-col bg-[var(--color-bg-primary)]'>
      {/* Top bar */}
      <header className='flex h-16 shrink-0 items-center justify-between gap-3 border-b border-brand-600 bg-[var(--color-bg-primary)] px-4 md:gap-8 md:px-8'>
        <div className='flex min-w-0 items-center gap-3 md:gap-5'>
          <Link to='/'>
            <img
              src='/ahoy-logo-with-text-black.svg'
              alt='Ahoy'
              className='h-7'
            />
          </Link>
          <span aria-hidden className='h-7 w-px shrink-0 bg-brand-600/40' />
          <span className='truncate font-mono text-sm tracking-wide text-brand-600'>
            {title}
          </span>
        </div>
        <div className='flex shrink-0 items-center gap-5 md:gap-8'>
          <Link
            to={backTo}
            aria-describedby='self-service-back-desc'
            className='font-mono text-sm tracking-wide text-[var(--color-text-primary)] hover:text-brand-600'
          >
            Back
          </Link>
          <span id='self-service-back-desc' className='sr-only'>
            Return to the previous step
          </span>
          <Link
            to={leaveTo}
            aria-describedby='self-service-leave-desc'
            className='font-mono text-sm tracking-wide text-[var(--color-text-primary)] hover:text-brand-600'
          >
            Leave
          </Link>
          <span id='self-service-leave-desc' className='sr-only'>
            Leave workspace setup and return to Ahoy
          </span>
        </div>
      </header>

      {/* Body */}
      <div className='flex min-h-0 flex-1'>
        <StepSidebar
          currentIndex={currentStepIndex}
          steps={steps}
          stepPath={stepPath}
        />

        {/* Content area. The artwork and footer links are siblings of the
            scroll region (not children) so they stay pinned while the step
            content scrolls. */}
        {/* NOTE(design-system): #FBFAF8 is a warm off-white specific to the
            onboarding artwork surface — it has no theme.css token today, so it
            stays a literal rather than drifting the design onto the nearest
            semantic token. Replace with a token when one is minted. */}
        <main className='relative min-h-0 flex-1 bg-[#FBFAF8] dark:bg-[var(--color-bg-secondary)]'>
          {/* Faint ring-buoy patent artwork behind the content */}
          <div
            aria-hidden
            className='ring-buoy-patent-bg pointer-events-none absolute inset-0 bg-contain bg-center bg-no-repeat opacity-40 grayscale'
          />
          <div className='relative h-full overflow-y-auto'>
            <div className='mx-auto w-full max-w-2xl px-6 pt-16 pb-28 md:pt-24'>
              {children}
            </div>
          </div>

          {/* Footer links */}
          <div className='absolute right-6 bottom-6 flex items-center gap-8 md:right-10'>
            {/* TODO(self-service): point at the real Help Center when it has a home */}
            <span className='font-mono text-sm tracking-wide text-brand-600'>
              Help Center
            </span>
            <a
              href='mailto:help@ahoy.ai'
              className='font-mono text-sm tracking-wide text-brand-600 hover:text-brand-700'
            >
              Contact support
            </a>
          </div>
        </main>
      </div>
    </div>
  );
}
